import React, { useState } from "react";

import googleLogo from "../../../_assets/img/google-logo.svg";
import microsoftLogo from "../../../_assets/img/microsoft-logo.svg";
import { startGoogleRegistration, startMicrosoftRegistration, mapSsoError } from "../registrationSso";
import ArrowRight from "../components/ArrowRight";
import AlreadyHaveAccount from "../components/AlreadyHaveAccount";
import CheckBadge from "../components/CheckBadge";
import Spinner from "../components/Spinner";

// Simple RFC-ish email format check (mockup only checks non-empty — spec requires format validation).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FALLBACK_ERROR = "Die Registrierung ist fehlgeschlagen. Bitte versuche es erneut.";

// The axios interceptors reject with a plain string in most cases (e.g. the backend
// errorMessage, "Unauthorized", a network message), so handle strings first.
const extractErrorMessage = (err) => {
    if (typeof err === "string" && err.trim()) return err;
    return err?.errorMessage || err?.message || FALLBACK_ERROR;
};

/**
 * Step 3 — "Wie lautet deine E-Mail?"
 * Email input (format-validated) + "ODER" divider + Google/Microsoft SSO buttons.
 * No password field. Trust row + Nutzungsvereinbarung legal line.
 *
 * onSsoSuccess receives the SSO auth result ({provider, idToken, email, ...}) so the
 * registration API call downstream can create the account with the SSO identity.
 */
const EmailStep = ({ email, setEmail, onEmailSubmit, onSsoSuccess, onMfaRequired }) => {
    const [touched, setTouched] = useState(false);
    const [pending, setPending] = useState(null); // "EMAIL" | "GOOGLE" | "MICROSOFT" | null
    const [error, setError] = useState(null);

    const isValid = EMAIL_REGEX.test(email.trim());
    const showValidationError = touched && email.length > 0 && !isValid;
    const busy = pending !== null;

    const submit = async (e) => {
        e.preventDefault();
        setTouched(true);
        if (!isValid || busy) return;
        setError(null);
        setPending("EMAIL");
        try {
            await onEmailSubmit();
        } catch (err) {
            console.error("Registration (email) failed:", err);
            setError(extractErrorMessage(err));
        } finally {
            setPending(null);
        }
    };

    const runSso = async (provider, start) => {
        if (busy) return;
        setError(null);
        setPending(provider);
        try {
            const result = await start();
            if (result?.mfaRequired) {
                // Account has 2FA (existing user) → hand off to the 2FA code prompt (keep the provider).
                onMfaRequired(provider);
                return;
            }
            await onSsoSuccess(result);
        } catch (err) {
            console.error("Registration (SSO) failed:", err);
            // Firebase popup errors carry a `code` → map to a friendly message.
            // API errors from the registration call come through as strings → surface them.
            const message = err?.code ? mapSsoError(err) : extractErrorMessage(err);
            if (message) setError(message);
        } finally {
            setPending(null);
        }
    };

    return (
        <form className="registration-step registration-step--email" onSubmit={submit} data-cy="registration-step-email">
            <p className="registration-step__eyebrow">Letzter Schritt</p>
            <h1 className="registration-step__title">Wie lautet deine E-Mail?</h1>

            <input
                type="email"
                className="registration-input"
                placeholder="Deine E-Mail eingeben"
                aria-label="E-Mail"
                aria-invalid={showValidationError || !!error}
                aria-describedby={showValidationError ? "registration-email-error-msg" : undefined}
                value={email}
                onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null); // clear a stale submit error once the user edits the email
                }}
                onBlur={() => setTouched(true)}
                disabled={busy}
                autoFocus
                data-cy="registration-email-input"
            />
            {showValidationError && (
                <p className="registration-input__error" id="registration-email-error-msg" role="alert" data-cy="registration-email-error">
                    Bitte gib eine gültige E-Mail-Adresse ein.
                </p>
            )}

            <button type="submit" className="registration-cta" disabled={!isValid || busy} data-cy="registration-email-next">
                {pending === "EMAIL" ? (
                    <>
                        <Spinner /> Wird erstellt …
                    </>
                ) : (
                    <>
                        Weiter <ArrowRight />
                    </>
                )}
            </button>

            <div className="registration-divider">ODER</div>

            <button
                type="button"
                className="registration-sso"
                onClick={() => runSso("GOOGLE", startGoogleRegistration)}
                disabled={busy}
                data-cy="registration-sso-google"
            >
                {pending === "GOOGLE" ? (
                    <Spinner />
                ) : (
                    <img className="registration-sso__logo" src={googleLogo} alt="" aria-hidden />
                )}
                {pending === "GOOGLE" ? "Wird angemeldet …" : "Mit Google registrieren"}
            </button>
            <button
                type="button"
                className="registration-sso"
                onClick={() => runSso("MICROSOFT", startMicrosoftRegistration)}
                disabled={busy}
                data-cy="registration-sso-microsoft"
            >
                {pending === "MICROSOFT" ? (
                    <Spinner />
                ) : (
                    <img className="registration-sso__logo" src={microsoftLogo} alt="" aria-hidden />
                )}
                {pending === "MICROSOFT" ? "Wird angemeldet …" : "Mit Microsoft registrieren"}
            </button>

            {error && (
                <p className="registration-input__error" role="alert" data-cy="registration-sso-error">
                    {error}
                </p>
            )}

            <AlreadyHaveAccount />

            <div className="registration-trust">
                <span className="registration-trust__item">
                    <CheckBadge /> 100% Geld-zurück-Garantie
                </span>
                <span className="registration-trust__item">
                    <CheckBadge /> Jederzeit kündbar
                </span>
            </div>

            <p className="registration-legal">
                Mit der Registrierung stimmst du unserer{" "}
                <a
                    href="https://app.pathway-solutions.de/Nutzungsvereinbarung_Platform.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-cy="registration-terms-link"
                >
                    Nutzungsvereinbarung
                </a>{" "}
                zu.
            </p>
        </form>
    );
};

export default EmailStep;
